import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../../common/Button';
import { Card } from '../../common/Card';
import { LoadingState, EmptyState } from '../../common';
import { Heading, Text } from '../../common/Typography';
import SessionNavigationButton from '../../common/SessionNavigationButton';
import PlannedSessionsTable from '../PlannedSessionsTable';
import ManageBlockModal from '../ManageBlockModal';
import { getSessionBlock } from '../../../services/blockBookingService';
import {
  updateSession,
  updateSessionStatus,
  cancelSession,
  uncancelSession,
  deleteSession,
} from '../../../services/sessionManagementService';
import { getSessionStatusLabel, getSessionDateString } from './sessionHubUtils';
import SessionSeriesBadge from '../SessionSeriesBadge';
import { inferBlockSeries } from '../../../utils/sessionSeries';

const PLANNED_STATUSES = new Set(['upcoming', 'draft', 'cancelled']);

const ManageBlockIconButton = ({ onClick }) => (
  <Button
    variant="ghost"
    size="small"
    onClick={onClick}
    aria-label="Edit block booking"
    title="Edit block booking"
    className="shrink-0 px-2"
  >
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
      />
    </svg>
  </Button>
);

const sortBlockSessions = (sessions) =>
  [...(sessions || [])].sort((a, b) => {
    const dateCompare = (a.session_date || '').localeCompare(b.session_date || '');
    if (dateCompare !== 0) return dateCompare;
    return (a.start_time || '').localeCompare(b.start_time || '');
  });

const BlockSessionsPanel = ({
  clubId,
  blockId,
  selectedClub,
  currentUser,
  onBlockLoaded,
  onBlockRemoved,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [block, setBlock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showManageBlock, setShowManageBlock] = useState(false);
  const [mutationError, setMutationError] = useState(null);

  const fetchBlock = useCallback(async () => {
    if (!blockId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getSessionBlock(blockId);
      if (!result.success || !result.data) {
        setBlock(null);
        setError(result.error || 'This block booking could not be loaded.');
        onBlockLoaded?.(null);
        return;
      }
      if (result.data.club_id && result.data.club_id !== clubId) {
        setBlock(null);
        setError('This block booking is not part of this club.');
        onBlockLoaded?.(null);
        return;
      }
      setBlock(result.data);
      onBlockLoaded?.(result.data);
    } catch (err) {
      console.error('Error loading block sessions:', err);
      setBlock(null);
      setError(err.message || 'Failed to load block sessions');
      onBlockLoaded?.(null);
    } finally {
      setLoading(false);
    }
  }, [blockId, clubId, onBlockLoaded]);

  useEffect(() => {
    fetchBlock();
  }, [fetchBlock]);

  const sessions = useMemo(
    () =>
      sortBlockSessions(block?.sessions).map((session) => ({
        ...session,
        is_block_session: true,
        clubs: selectedClub,
      })),
    [block, selectedClub]
  );

  const plannedSessions = useMemo(
    () => sessions.filter((session) => PLANNED_STATUSES.has(session.session_status)),
    [sessions]
  );

  const otherSessions = useMemo(
    () => sessions.filter((session) => !PLANNED_STATUSES.has(session.session_status)),
    [sessions]
  );

  const runSessionMutation = async (action, fallbackMessage) => {
    try {
      setMutationError(null);
      await action();
      await fetchBlock();
    } catch (err) {
      console.error(fallbackMessage, err);
      setMutationError(err.message || fallbackMessage);
      throw err;
    }
  };

  const handleEditSession = (sessionId, updates) =>
    runSessionMutation(() => updateSession(sessionId, updates), 'Failed to update session');

  const handleUpdateSessionStatus = (sessionId, newStatus) =>
    runSessionMutation(() => updateSessionStatus(sessionId, newStatus), 'Failed to update session status');

  const handleCancelSession = (sessionId) =>
    runSessionMutation(() => cancelSession(sessionId), 'Failed to cancel session');

  const handleUncancelSession = (sessionId) =>
    runSessionMutation(() => uncancelSession(sessionId), 'Failed to restore session');

  const handleDeleteSession = (sessionOrId) => {
    const sessionId = typeof sessionOrId === 'object' ? sessionOrId?.id : sessionOrId;
    if (!sessionId) return Promise.resolve();
    return runSessionMutation(() => deleteSession(sessionId), 'Failed to delete session');
  };

  const handleManageSignups = (sessionId) => {
    navigate(`/club/${clubId}/sessions/${sessionId}/signups`, {
      state: { ...location.state, fromBlockSetup: true, blockId },
    });
  };

  const handleManageBlockSuccess = async () => {
    const result = await getSessionBlock(blockId);
    if (!result.success || !result.data) {
      onBlockRemoved?.();
      return;
    }
    await fetchBlock();
  };

  if (loading) {
    return <LoadingState message="Loading block sessions…" />;
  }

  if (error || !block) {
    return (
      <Card variant="default" size="medium" className="py-12 text-center">
        <Heading level={2} className="mb-4">Block unavailable</Heading>
        <Text className="mb-6 text-text-secondary">{error || 'This block booking could not be loaded.'}</Text>
        <Button onClick={onBlockRemoved} variant="primary">
          Back to Block bookings
        </Button>
      </Card>
    );
  }

  const todayStr = new Date().toISOString().split('T')[0];
  const blockSeries = inferBlockSeries(sessions);
  const futureCount = sessions.filter((session) => {
    const dateStr = getSessionDateString(session);
    return dateStr && dateStr >= todayStr && PLANNED_STATUSES.has(session.session_status) && session.session_status !== 'cancelled';
  }).length;

  const openManageBlock = () => setShowManageBlock(true);

  return (
    <div className="space-y-6">
      <Text className="text-sm text-text-secondary">
        {blockSeries?.mixed ? 'Mixed series' : blockSeries?.name ? blockSeries.name : null}
        {blockSeries?.name || blockSeries?.mixed ? ' · ' : ''}
        {sessions.length} session{sessions.length !== 1 ? 's' : ''}
        {futureCount > 0 ? ` · ${futureCount} upcoming` : ''}
        {block.description ? ` · ${block.description}` : ''}
      </Text>

      {mutationError ? (
        <Card variant="danger" size="medium">
          <Text className="text-sm">{mutationError}</Text>
        </Card>
      ) : null}

      {sessions.length === 0 ? (
        <Card variant="default" size="medium" className="!p-4 sm:!p-6">
          <div className="mb-2 flex items-start justify-end">
            <ManageBlockIconButton onClick={openManageBlock} />
          </div>
          <EmptyState
            title="No sessions in this block"
            description="Edit the block to change settings, or delete it if it is no longer needed."
          />
        </Card>
      ) : (
        <>
          {plannedSessions.length > 0 ? (
            <Card variant="default" size="medium" className="!p-4 sm:!p-6">
              <div className="mb-4 flex items-start justify-between gap-3">
                <Heading level={2} className="min-w-0 text-lg font-bold text-text-primary">
                  Planned Sessions in Block Booking
                </Heading>
                <ManageBlockIconButton onClick={openManageBlock} />
              </div>
              <PlannedSessionsTable
                sessions={plannedSessions}
                clubId={clubId}
                onEditSession={handleEditSession}
                onUpdateSessionStatus={handleUpdateSessionStatus}
                onCancelSession={handleCancelSession}
                onUncancelSession={handleUncancelSession}
                onDeleteSession={handleDeleteSession}
                onManageSignups={handleManageSignups}
                currentUser={currentUser}
              />
            </Card>
          ) : null}

          {otherSessions.length > 0 ? (
            <Card variant="default" size="medium" className="!p-4 sm:!p-6">
              <div className="mb-4 flex items-start justify-between gap-3">
                <Heading level={2} className="min-w-0 text-lg font-bold text-text-primary">
                  Started and completed
                </Heading>
                {plannedSessions.length === 0 ? (
                  <ManageBlockIconButton onClick={openManageBlock} />
                ) : null}
              </div>
              <ul className="space-y-3">
                {otherSessions.map((session) => (
                  <li
                    key={session.id}
                    className="flex flex-col gap-3 rounded-xl border border-border-medium bg-surface p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-text-primary truncate">
                          {session.name || 'Untitled session'}
                        </span>
                        <span className="text-xs text-text-muted">
                          {getSessionStatusLabel(session.session_status)}
                        </span>
                        <SessionSeriesBadge session={session} />
                      </div>
                      <Text className="mt-1 text-sm text-text-secondary">
                        {getSessionDateString(session) || '—'}
                        {session.start_time ? ` · ${String(session.start_time).substring(0, 5)}` : ''}
                      </Text>
                    </div>
                    {session.session_status === 'ongoing' ? (
                      <SessionNavigationButton
                        session={session}
                        clubId={clubId}
                        action="enter"
                        variant="primary"
                        size="small"
                        className="w-full sm:w-auto"
                      >
                        Enter session
                      </SessionNavigationButton>
                    ) : session.session_status === 'completed' ? (
                      <Button
                        variant="outline"
                        size="small"
                        className="w-full sm:w-auto"
                        onClick={() => navigate(`/club/${clubId}/session-wrapup/${session.id}`)}
                      >
                        Review
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </>
      )}

      {showManageBlock ? (
        <ManageBlockModal
          blockId={blockId}
          onClose={() => setShowManageBlock(false)}
          onSuccess={handleManageBlockSuccess}
        />
      ) : null}
    </div>
  );
};

export default BlockSessionsPanel;
